import { ref } from 'vue'

// The UI scale factor, for sizes that are set from JavaScript.
//
// postcss-pxtorem converts the stylesheets' px to rem at build time, and
// `html { font-size }` (App.vue) sets the root to 16px × --ui-scale, 0.9 by
// default so the app matches a browser zoomed to 90%. CSS follows that for
// free. Anything sized in JavaScript — chart fonts, canvas text, grid row
// heights, measured layout constants — does not, so it multiplies by this.
//
//   import { uiScale, px } from '@/utils/uiScale'
//   fontSize: px(12)          // 10.8 at the default scale
//
// Read live rather than cached: the root can change under a user setting,
// and phones (below 760px) stay at 16px, so the factor is 1 there.
// Bumped by applyUiScale(). uiScale() reads it, so any computed or template
// that calls uiScale()/px()/$px() re-renders when the user changes Text Size.
const generation = ref(0)

export function uiScale() {
    void generation.value
    if (typeof document === 'undefined') return 1
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize)
    return fs > 0 ? fs / 16 : 1
}

/** A design-time pixel value at the current scale, to 2 decimals. */
export function px(n) {
    return Math.round(n * uiScale() * 100) / 100
}

// ---- User preference -------------------------------------------------------
//
// The root font-size rule in App.vue is `16px × var(--ui-scale, 0.9)`.
// index.html seeds --ui-scale from this localStorage key before first paint;
// the sidebar's Text Size control writes it and applies it live.
export const UI_SCALE_KEY = 'genoplot_ui_scale'
export const UI_SCALE_DEFAULT = 0.9
/** Fired on window after applyUiScale(); detail.scale carries the new value. */
export const UI_SCALE_EVENT = 'genoplot:uiscale'

/** The three stops the Text Size control offers. Medium is the app default. */
export const UI_SCALE_PRESETS = [
    { id: 'small', label: 'Small', scale: 0.8 },
    { id: 'medium', label: 'Medium', scale: UI_SCALE_DEFAULT },
    { id: 'large', label: 'Large', scale: 1 },
]

/** The stored scale, or the default when unset / invalid. */
export function readUiScale() {
    try {
        const s = parseFloat(localStorage.getItem(UI_SCALE_KEY))
        if (s > 0.5 && s < 2) return s
    } catch (e) { /* private mode */ }
    return UI_SCALE_DEFAULT
}

/** The preset id matching a scale (nearest stop), for the control's state. */
export function uiScalePresetId(scale) {
    let best = UI_SCALE_PRESETS[0]
    for (const p of UI_SCALE_PRESETS) {
        if (Math.abs(p.scale - scale) < Math.abs(best.scale - scale)) best = p
    }
    return best.id
}

/**
 * Persist and apply a scale. CSS follows at once through --ui-scale; a
 * resize event nudges the autoresize charts so their JS-set sizes (px())
 * are rebuilt on the next option pass.
 */
export function applyUiScale(scale) {
    try { localStorage.setItem(UI_SCALE_KEY, String(scale)) } catch (e) { /* private mode */ }
    if (typeof document === 'undefined') return
    document.documentElement.style.setProperty('--ui-scale', String(scale))
    generation.value++
    // Imperative consumers (vModal re-parses its box; charts autoresize).
    window.dispatchEvent(new CustomEvent(UI_SCALE_EVENT, { detail: { scale } }))
    window.dispatchEvent(new Event('resize'))
}
